import { useCallback, useEffect, useState } from 'react';
import { decodeJwt } from 'jose';
import { getOrCreateUserUUID } from '@/helpers/Cookie';
const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;
export default function useConnectionDetails(appConfig, assetsBasePath = '') {
    // Generate room connection details, including:
    //   - A random Room name
    //   - A random Participant name
    //   - An Access Token to permit the participant to join the room
    //   - The URL of the LiveKit server to connect to
    //
    // In real-world application, you would likely allow the user to specify their
    // own participant name, and possibly to choose from existing rooms to join.
    const [connectionDetails, setConnectionDetails] = useState(null);
    const fetchConnectionDetails = useCallback(async () => {
        var _a;
        setConnectionDetails(null);
        const baseUrl = assetsBasePath ||
            (typeof window !== 'undefined' && window.__AGENT_BASE_PATH__) ||
            (typeof window !== 'undefined' ? window.location.origin : '');
        const url = new URL('/api/connection-details', baseUrl);
        let data;
        try {
            const res = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Sandbox-Id': (_a = appConfig.sandboxId) !== null && _a !== void 0 ? _a : '',
                },
                body: JSON.stringify({
                    room_config: appConfig.agentName
                        ? {
                            agents: [{ agent_name: appConfig.agentName }],
                        }
                        : undefined,
                    user_uuid: getOrCreateUserUUID(),
                    navigation_open: typeof window !== 'undefined' && sessionStorage.getItem('agentOpen') === 'true',
                }),
            });
            data = await res.json();
        }
        catch (error) {
            console.error('Error fetching connection details:', error);
            throw new Error('Error fetching connection details!');
        }
        setConnectionDetails(data);
        return data;
    }, []);
    useEffect(() => {
        fetchConnectionDetails();
    }, [fetchConnectionDetails]);
    const isConnectionDetailsExpired = useCallback(() => {
        const token = connectionDetails === null || connectionDetails === void 0 ? void 0 : connectionDetails.participantToken;
        if (!token) {
            return true;
        }
        const jwtPayload = decodeJwt(token);
        if (!jwtPayload.exp) {
            return true;
        }
        const expiresAt = new Date(jwtPayload.exp - ONE_MINUTE_IN_MILLISECONDS);
        const now = new Date();
        return expiresAt >= now;
    }, [connectionDetails === null || connectionDetails === void 0 ? void 0 : connectionDetails.participantToken]);
    const existingOrRefreshConnectionDetails = useCallback(async () => {
        if (isConnectionDetailsExpired() || !connectionDetails) {
            return fetchConnectionDetails();
        }
        else {
            return connectionDetails;
        }
    }, [connectionDetails, fetchConnectionDetails, isConnectionDetailsExpired]);
    return {
        connectionDetails,
        refreshConnectionDetails: fetchConnectionDetails,
        existingOrRefreshConnectionDetails,
    };
}
